import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminService } from '../../../services/adminService';
import { parsePeriod } from '../../../constants';
import { Loading, EmptyState } from '../../../shared/components';
import './AdminCommonStyles.scss';

const normalizePeriod = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  return /^\d{6}$/.test(raw) ? raw : '';
};
const normalizeReturnTo = (value) => {
  const raw = String(value || '').trim();
  if (!raw.startsWith('/admin/uniform-reports')) return '';
  return raw;
};

const monthInputToPeriod = (value) => (value ? value.replace('-', '') : '');
const periodToMonthInput = (value) => (value && value.length === 6 ? `${value.slice(0, 4)}-${value.slice(4, 6)}` : '');

const getPeriodLabel = (period) => {
  const normalized = normalizePeriod(period);
  if (!normalized) return '-';
  const parsed = parsePeriod(normalized);
  if (!parsed.month || !parsed.year || !parsed.label) return normalized;
  return parsed.label;
};

const getComparisonStatusLabel = (status) => {
  switch (status) {
    case 'changed':
      return 'Changed';
    case 'same':
      return 'Same';
    case 'added':
      return 'Added';
    case 'removed':
      return 'Removed';
    case 'missing_in_current':
      return 'Missing in Current';
    default:
      return status || '-';
  }
};

const getComparisonStatusClass = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'unknown';
  if (['changed', 'same', 'added', 'removed', 'missing_in_current'].includes(normalized)) return normalized;
  return 'unknown';
};

const getDiffValue = (item) => {
  const qtyDiff = Number(item.qty_diff);
  if (Number.isFinite(qtyDiff)) return qtyDiff;

  const diff = Number(item.difference);
  if (Number.isFinite(diff)) return diff;

  const currentQty = Number(item.current_qty);
  const previousQty = Number(item.previous_qty);
  if (Number.isFinite(currentQty) && Number.isFinite(previousQty)) return currentQty - previousQty;
  return 0;
};

const UniformComparisonPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('user_id') || '';
  const reportId = searchParams.get('report_id') || '';
  const requestedPeriod = normalizePeriod(searchParams.get('period'));
  const returnTo = normalizeReturnTo(searchParams.get('return_to'));

  const [periodInput, setPeriodInput] = useState(periodToMonthInput(requestedPeriod));
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [toast, setToast] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [items, setItems] = useState([]);
  const [notesByDetailId, setNotesByDetailId] = useState({});
  const [savingByDetailId, setSavingByDetailId] = useState({});

  useEffect(() => {
    setPeriodInput(periodToMonthInput(requestedPeriod));
  }, [requestedPeriod]);

  const showToast = (message) => {
    if (!message) return;
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  const loadComparison = useCallback(async (periodValue) => {
    if (!userId || !periodValue) {
      setErrorMessage('Parameter user_id dan period wajib diisi.');
      setComparisonData(null);
      setItems([]);
      setNotesByDetailId({});
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const res = await adminService.getUniformComparisonByUser(userId, { period: periodValue });
      const data = res?.data || null;
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setComparisonData(data);
      setItems(nextItems);

      const nextNotes = {};
      nextItems.forEach((item) => {
        const detailId = item.current_uniform_report_detail_id;
        if (detailId) {
          nextNotes[detailId] = item.comparison_notes || '';
        }
      });
      setNotesByDetailId(nextNotes);
    } catch (err) {
      setComparisonData(null);
      setItems([]);
      setNotesByDetailId({});
      setErrorMessage(err?.message || 'Gagal memuat data perbandingan.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadComparison(requestedPeriod);
  }, [requestedPeriod, loadComparison]);

  const handleApplyPeriod = (event) => {
    event.preventDefault();
    const nextPeriod = monthInputToPeriod(periodInput);
    if (!nextPeriod) {
      showToast('Pilih periode terlebih dahulu.');
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('period', nextPeriod);
    setSearchParams(nextParams);
  };

  const handleSaveComparisonNotes = async (item) => {
    const detailId = item.current_uniform_report_detail_id;
    if (!detailId) {
      showToast('Detail report current tidak ditemukan untuk item ini.');
      return;
    }

    const comparisonNotes = notesByDetailId[detailId] || '';
    setSavingByDetailId((prev) => ({ ...prev, [detailId]: true }));

    try {
      const res = await adminService.updateReportDetail(detailId, { comparison_notes: comparisonNotes });
      setItems((prev) =>
        prev.map((row) =>
          row.current_uniform_report_detail_id === detailId
            ? { ...row, comparison_notes: comparisonNotes }
            : row
        )
      );
      showToast(res?.message || 'Catatan perbandingan berhasil disimpan.');
    } catch (err) {
      showToast(err?.message || 'Gagal menyimpan catatan perbandingan.');
    } finally {
      setSavingByDetailId((prev) => ({ ...prev, [detailId]: false }));
    }
  };

  const handleBackToList = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate('/admin/uniform-reports');
  };

  const headerMeta = useMemo(() => {
    if (!comparisonData) {
      return {
        employeeName: '-',
        currentPeriod: requestedPeriod,
        previousPeriod: '',
        previousDataAvailable: false,
      };
    }

    return {
      employeeName: comparisonData.employee_name || '-',
      currentPeriod: comparisonData.current_period || requestedPeriod,
      previousPeriod: comparisonData.previous_period || '',
      previousDataAvailable: Boolean(comparisonData.previous_data_available),
    };
  }, [comparisonData, requestedPeriod]);

  if (loading) return <Loading />;

  return (
    <div className="page-container admin-compare-page">
      {toast && <div className="toast">{toast}</div>}

      <div className="detail-card admin-compare-header">
        <div>
          <h3>Perbandingan Seragam Employee</h3>
          <p>
            Employee: <strong>{headerMeta.employeeName}</strong>
          </p>
          <p>
            Current: <strong>{getPeriodLabel(headerMeta.currentPeriod)}</strong>
            {' | '}
            Previous: <strong>{getPeriodLabel(headerMeta.previousPeriod)}</strong>
          </p>
        </div>
        <div className="admin-compare-header__actions">
          <button type="button" className="btn-secondary" onClick={handleBackToList}>
            Kembali ke List
          </button>
          {reportId && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(`/admin/uniform-reports/${reportId}`)}
            >
              Buka Detail Report
            </button>
          )}
        </div>
      </div>

      <div className="detail-card">
        <form className="admin-compare-filter" onSubmit={handleApplyPeriod}>
          <div className="form-group">
            <label htmlFor="comparison-period">Periode Compare</label>
            <input
              id="comparison-period"
              type="month"
              value={periodInput}
              onChange={(event) => setPeriodInput(event.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary">Terapkan</button>
        </form>

        {comparisonData && !headerMeta.previousDataAvailable && (
          <div className="admin-compare-warning">
            Data previous period tidak tersedia. Anda tetap bisa mengisi catatan perbandingan untuk data current.
          </div>
        )}

        {errorMessage ? (
          <EmptyState icon="!" title="Gagal memuat data compare" desc={errorMessage} />
        ) : items.length === 0 ? (
          <EmptyState icon="LIST" title="Tidak ada item compare" desc="Belum ada detail seragam untuk periode ini." />
        ) : (
          <div className="table-card admin-compare-table-wrap">
            <table className="data-table admin-compare-table">
              <thead>
                <tr>
                  <th>Seragam</th>
                  <th>Qty Current</th>
                  <th>Qty Previous</th>
                  <th>Selisih</th>
                  <th>Foto Current</th>
                  <th>Foto Previous</th>
                  <th>Notes Current</th>
                  <th>Notes Previous</th>
                  <th>Status</th>
                  <th>Comparison Notes</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const detailId = item.current_uniform_report_detail_id;
                  const rowKey = detailId || item.uniform_id || `compare-row-${index}`;
                  const diffValue = getDiffValue(item);
                  const isSaving = Boolean(savingByDetailId[detailId]);
                  const status = item.comparison_status || '';
                  const statusClass = getComparisonStatusClass(status);
                  const isCurrentMissing = statusClass === 'missing_in_current';

                  return (
                    <tr key={rowKey}>
                      <td data-label="Seragam"><strong>{item.uniform_name || `Uniform #${item.uniform_id}`}</strong></td>
                      <td data-label="Qty Current">{Number(item.current_qty) || 0}</td>
                      <td data-label="Qty Previous">{Number(item.previous_qty) || 0}</td>
                      <td
                        data-label="Selisih"
                        className={`admin-compare-diff${diffValue > 0 ? ' admin-compare-diff--up' : diffValue < 0 ? ' admin-compare-diff--down' : ''}`}
                      >
                        {diffValue > 0 ? '+' : ''}{diffValue}
                      </td>
                      <td data-label="Foto Current" className="admin-compare-photo-cell">
                        {item.current_url_photo ? (
                          <a href={item.current_url_photo} target="_blank" rel="noreferrer">
                            <img src={item.current_url_photo} alt="Current uniform" className="admin-compare-photo" />
                          </a>
                        ) : (
                          <span className="admin-compare-photo-empty">-</span>
                        )}
                      </td>
                      <td data-label="Foto Previous" className="admin-compare-photo-cell">
                        {item.previous_url_photo ? (
                          <a href={item.previous_url_photo} target="_blank" rel="noreferrer">
                            <img src={item.previous_url_photo} alt="Previous uniform" className="admin-compare-photo" />
                          </a>
                        ) : (
                          <span className="admin-compare-photo-empty">-</span>
                        )}
                      </td>
                      <td data-label="Notes Current" className="admin-compare-text-cell">{item.current_notes || '-'}</td>
                      <td data-label="Notes Previous" className="admin-compare-text-cell">{item.previous_notes || '-'}</td>
                      <td data-label="Status">
                        <span className={`admin-compare-status admin-compare-status--${statusClass}`}>
                          {getComparisonStatusLabel(status)}
                        </span>
                      </td>
                      <td data-label="Comparison Notes" className="admin-compare-notes-cell">
                        <textarea
                          className="admin-compare-notes"
                          value={notesByDetailId[detailId] || ''}
                          onChange={(event) =>
                            setNotesByDetailId((prev) => ({ ...prev, [detailId]: event.target.value }))
                          }
                          placeholder="Isi catatan perbandingan..."
                          rows={3}
                          disabled={!detailId}
                        />
                      </td>
                      <td data-label="Aksi" className="admin-compare-action-cell">
                        <button
                          type="button"
                          className="btn-sm btn-primary"
                          disabled={!detailId || isSaving || isCurrentMissing}
                          onClick={() => handleSaveComparisonNotes(item)}
                        >
                          {isSaving ? 'Menyimpan...' : 'Simpan'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniformComparisonPage;
